import {
  AudienceComparisonCellData,
  AudienceComparisonColumn,
} from "../../types/audience-compare";

import InfoTooltip from "../info-tooltip/InfoTooltip";
import Loader from "../loader/Loader";
import { ReactNode } from "react";
import { Tooltip } from "@mantine/core";
import classNames from "classnames";
import css from "./ResultCell.module.css";

export type ResultCellProps = {
  cell: AudienceComparisonCellData;
  columnKind: AudienceComparisonColumn;
  renderSuccess: (
    x: Extract<AudienceComparisonCellData, { kind: "success" | "alert" }>,
  ) => ReactNode;
  className?: string;
};
/**
 * Renders a data cell in the results table while also accounting for
 * all possible states: loading, success, n-a, and error.
 *
 * The success state is rendered by the `renderSuccess` function
 * which allows you to customize the success state.
 */
export const ResultCell = ({
  cell,
  columnKind,
  renderSuccess,
  className,
}: ResultCellProps) => {
  return (
    <td className={classNames("text-center", className)}>
      {cell.kind === "loading" && <Loader size="sm" color="light" />}
      {cell.kind === "success" && renderSuccess(cell)}
      {cell.kind === "alert" && (
        <>
          {renderSuccess(cell)}
          <InfoTooltip
            label={`This index is still collecting responses, so it may not be reliable. We are ${cell.confidencePct}% confident of the result.`}
            className="fas fa-exclamation-triangle text-primary ms-2"
          />
        </>
      )}
      {cell.kind === "n-a" && (
        <Tooltip
          label={`Limitations in baseline or audience data prevent ${columnKind} calculation`}
        >
          <span>N/A</span>
        </Tooltip>
      )}
      {cell.kind === "error" && (
        <Tooltip
          multiline
          w={350}
          withArrow
          arrowSize={6}
          events={{ hover: true, focus: true, touch: true }}
          label={
            <div>
              We encountered an issue. Your request either timed out or the data
              intersection specified is not valid.
            </div>
          }
        >
          <span className={css["error-text"]}>--</span>
        </Tooltip>
      )}
    </td>
  );
};
